import PaymentService from "../../services/payment/PaymentService";
import CustomerService from "../../services/customers/CustomersService";
import GLCoaService from "../../services/glcoa/GLCoaService";
import { get, round } from "lodash";
import StorageService from "../../services/storage/StorageService";

const StorageConstants = {
  CASHIER: "CASHIER"
};

const OnePayConfigAdapter = {
  adapt: paymentData => {
    return {
      toJson: () => {
        return {
          byPass: getByPassConfig(paymentData),
          close: getCloseConfig(),
          merchantDropDown: getMerchantDropDownConfig(paymentData),
          partsService: getPartsServiceConfig(paymentData),
          inviteToPay: getInviteToPayConfig(paymentData),
          accounting: getAccountingConfig(paymentData),
          accountingInviteToPay: getAccountingInviteToPayConfig(paymentData),
          cancel: getCancelConfig(paymentData)
        };
      }
    };
  }
};

export default OnePayConfigAdapter;

const getPartsServiceConfig = paymentData => {
  const isPartsService =
    !helper.isAccounting(paymentData) &&
    !helper.isCancelPayment(paymentData) &&
    !helper.isAccountingInviteToPay(paymentData) &&
    paymentData.permissions.showPayNow;

  if (isPartsService) {
    // Whenever we have a partial payment
    // we show the cancel button
    const getPartsServiceCancelPaymentConfig = paymentData => {
      const isPartsServiceCancelPayment = paymentData.permissions.showRefunds;
      if (isPartsServiceCancelPayment) {
        let cancelAmount = round(
          get(
            paymentData,
            "externallyPaidAmount",
            paymentData.totalAmount - paymentData.balanceDue
          ),
          2
        );
        const amountToRefund =
          paymentData.externallyPaidAmount || paymentData.balanceDue;
        return {
          showCancelPayment: cancelAmount > 0,
          amountToCancel: cancelAmount,
          amountToRefund,
          onSendCancelPayment: async payment => {
            const result = await PaymentService.sendCancelPayment(payment);

            if (result.isError) return result;

            window.location = result.paymentURL;
          }
        };
      }

      return {};
    };

    return {
      show: true,
      totalAmount: paymentData.totalAmount,
      amountToPay: paymentData.balanceDue,
      paidAmount: round(
        get(
          paymentData,
          "externallyPaidAmount",
          paymentData.totalAmount - paymentData.balanceDue
        ),
        2
      ),
      ...getPartsServiceCancelPaymentConfig(paymentData),
      onSendPayment: async payment => {
        const result = await PaymentService.sendPartsServicePayment(payment);

        if (result.isError) return result;

        window.location = result.paymentURL;
      }
    };
  }

  return undefined;
};

const getInviteToPayConfig = paymentData => {
  const isInviteToPay =
    !helper.isAccounting(paymentData) &&
    !helper.isAccountingInviteToPay(paymentData) &&
    !helper.isCancelPayment(paymentData) &&
    paymentData.permissions.showEbpp;

  if (isInviteToPay) {
    return {
      show: true,
      totalAmount: paymentData.totalAmount,
      amountToPay: paymentData.balanceDue,
      paidAmount: round(
        get(
          paymentData,
          "externallyPaidAmount",
          paymentData.totalAmount - paymentData.balanceDue
        ),
        2
      ),
      customerEmail: paymentData.customerEmail,
      customerPhone: paymentData.customerPhone,
      onSendPayment: async payment => {
        const result = await PaymentService.sendInviteToPayPayment(payment);
        window.scrollTo(0, 0);

        if (result.isSuccess)
          setTimeout(function() {
            window.close();
          }, 5000);

        return result;
      }
    };
  }

  return undefined;
};

const getAccountingConfig = paymentData => {
  if (helper.isAccounting(paymentData)) {
    const getDocumentId = documentId => {
      if (documentId.indexOf("_") !== -1)
        return documentId.substring(0, documentId.indexOf("_"));

      return documentId;
    };

    const accountingConfig = {
      show: true,
      receipt: getDocumentId(paymentData.documentId),
      customerEmail: paymentData.customerEmail,
      customerName: paymentData.customerName,
      customerNumber: paymentData.customerNumber,
      dealerStoresName: paymentData.dealerStoresName,
      onSendPayment: async payment => {
        const result = await PaymentService.sendAccountingPayment(payment);

        if (result.isError) return result;
        else if (result.paymentURL) window.location = result.paymentURL;
        else window.close();
      }
    };

    if (helper.isReturnPayment(paymentData)) {
      return {
        ...accountingConfig,
        returnAmount: paymentData.balanceDue * -1,
        amountToPay: paymentData.balanceDue
      };
    }

    return {
      ...accountingConfig
    };
  }

  return undefined;
};

const getAccountingInviteToPayConfig = paymentData => {
  if (helper.isAccountingInviteToPay(paymentData)) {
    const accountingInviteToPayConfig = {
      show: true,

      cashiers: get(paymentData, "cashiers", []).map(c => {
        return {
          ...c,
          selected:
            StorageService.getItem(StorageConstants.CASHIER) === c.cashierName
        };
      }),

      companyInfo: paymentData.companyInfo,

      onSendPayment: async payment => {
        const result = await PaymentService.sendAccountingInviteToPayPayment(
          payment
        );
        window.scrollTo(0, 0);

        if (result.isSuccess)
          setTimeout(function() {
            window.close();
          }, 5000);

        return result;
      },
      onSearchCustomer: async (customerName, customerType) => {
        const result = await CustomerService.getCustomers(
          customerName,
          customerType
        );

        return result;
      },
      onSelectAccount: async (companyNumber, accountId) => {
        const result = await GLCoaService.getGLAccount(
          companyNumber,
          accountId
        );
        return result;
      }
    };

    return {
      ...accountingInviteToPayConfig
    };
  }

  return undefined;
};

const getCancelConfig = paymentData => {
  const isCancelPayment =
    !helper.isAccounting(paymentData) &&
    !helper.isAccountingInviteToPay(paymentData) &&
    helper.isCancelPayment(paymentData);

  // Any payment which the amount to pay is 0.00
  // and the paid amount is positive
  if (isCancelPayment) {
    const cancelPaymentConfig = {
      show: true,
      totalAmount: paymentData.totalAmount,
      paidAmount: round(
        get(
          paymentData,
          "externallyPaidAmount",
          paymentData.totalAmount - paymentData.balanceDue
        ),
        2
      )
    };

    const amountToRefund =
      paymentData.externallyPaidAmount || paymentData.balanceDue;
    // Credit Memos are refunds
    if (helper.isCreditMemo(paymentData)) {
      return {
        ...cancelPaymentConfig,
        showRefundPayment: true,
        refundPermissionSettings: paymentData.permissions.showRefunds,
        validateInvoiceRefund : paymentData.validateInvoiceTransaction,
        amountToRefund,
        paidAmount: Math.abs(paymentData.totalAmount),
        onSendRefundPayment: async payment => {
          const result = await PaymentService.sendRefund(payment);

          if (result.isError) return result;

          window.location = result.paymentURL;
        }
      };
    }

    return {
      ...cancelPaymentConfig,
      isEdit: true,
      refundPermissionSettings: paymentData.permissions.showRefunds,
      validateInvoiceRefund : paymentData.validateInvoiceTransaction,
      showCancelPayment: true,
      amountToRefund,
      amountToCancel: round(
        get(
          paymentData,
          "externallyPaidAmount",
          paymentData.totalAmount - paymentData.balanceDue
        ),
        2
      ),
      onSendCancelPayment: async payment => {
        const result = await PaymentService.sendCancelPayment(payment);

        if (result.isError) return result;

        window.location = result.paymentURL;
      }
    };
  }

  return undefined;
};

const getByPassConfig = paymentData => {
  return {
    show: paymentData.permissions.showBypass && !paymentData.accountingI2PFlag,
    onByPass: async () => {
      const result = await PaymentService.sendBypassPayment();

      if (result.isError) return result;

      window.close();
    }
  };
};

const getCloseConfig = () => {
  return {
    show: true,
    onClose: () => {
      window.close();
    }
  };
};

const getMerchantDropDownConfig = paymentData => {
  return {
    show: !get(paymentData, "accountingI2PFlag", false)
  };
};

const helper = {
  isAccounting: paymentData => paymentData.accFlag,
  isAccountingInviteToPay: paymentData => paymentData.accountingI2PFlag,
  isCancelPayment: paymentData => paymentData.balanceDue <= 0,
  isCreditMemo: paymentData =>
    get(paymentData, "documentId", "")
      .toUpperCase()
      .startsWith("CM"),
  isReturnPayment: paymentData => paymentData.isReturn
};
